import { FormEvent, useState } from "react"
import { Head, Link, useForm } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { TableCard } from "@/components/blocks/TableCard"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

type ProgramRow = {
  id: number
  name: string
  slug: string
  active: boolean
  auto_enroll: boolean
  member_count: number
}

const slugify = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")

export default function AdminProgramsIndex({ programs }: { programs: ProgramRow[] }) {
  const [creating, setCreating] = useState(false)
  const [slugEdited, setSlugEdited] = useState(false)
  const form = useForm({ name: "", slug: "" })
  const description = "Programs group official content and decide who can see it."

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/admin/programs", { onSuccess: () => setCreating(false) })
  }

  return (
    <>
      <Head title="Programs">
        <meta name="description" content={description} />
        <meta property="og:title" content="Programs" />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Programs"
          description="Each program has its own official content, access codes, and instructors."
          actions={
            <Button type="button" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New program
            </Button>
          }
        />

        <div className="mt-6">
          <TableCard>
            <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
              <TableHead>
                <TableRow>
                  <TableHeader>Program</TableHeader>
                  <TableHeader className="hidden sm:table-cell">Settings</TableHeader>
                  <TableHeader className="text-right">Members</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.map((program) => (
                  <TableRow key={program.id}>
                    <TableCell>
                      <Link href={`/admin/programs/${program.id}`} className="block no-underline after:absolute after:inset-0 hover:text-ink-display">
                        <span className="block font-medium text-ink-display">{program.name}</span>
                        <span className="block text-xs text-ink-muted">/{program.slug}</span>
                      </Link>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="inline-flex flex-wrap gap-2">
                        {!program.active && <Badge tone="muted">Inactive</Badge>}
                        {program.auto_enroll && <Badge tone="neutral">Auto-enrolls new accounts</Badge>}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-ink-muted">
                      {program.member_count} {program.member_count === 1 ? "member" : "members"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>
        </div>

        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogContent>
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle>New program</DialogTitle>
                <DialogDescription>Add content to it from the official decks, quizzes, and exams pages.</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <label htmlFor="program-name">Name</label>
                  <Input
                    id="program-name"
                    required
                    autoFocus
                    value={form.data.name}
                    onChange={(e) => {
                      const name = e.target.value
                      form.setData((data) => ({ ...data, name, slug: slugEdited ? data.slug : slugify(name) }))
                    }}
                  />
                  {form.errors.name && <p className="text-xs text-danger-display">{form.errors.name}</p>}
                </div>
                <div className="space-y-2">
                  <label htmlFor="program-slug">URL name</label>
                  <Input
                    id="program-slug"
                    required
                    value={form.data.slug}
                    onChange={(e) => {
                      setSlugEdited(true)
                      form.setData("slug", e.target.value)
                    }}
                  />
                  <p className="text-xs text-ink-muted">Lowercase letters, numbers, and hyphens. Used in the program&rsquo;s links.</p>
                  {form.errors.slug && <p className="text-xs text-danger-display">{form.errors.slug}</p>}
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={form.processing}>
                  Create program
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </AdminShell>
    </>
  )
}
